/**
 * appKit — shared primitives for the portfolio apps.
 *
 * Every app needs a dock icon, a matching titlebar icon, and (for `type:"app"`
 * windows) a content component. These factories keep those consistent and
 * on-brand while the real, hand-designed app content is filled in. The icons
 * are self-contained SVGs (no external assets) so they render instantly on the
 * shell's critical path.
 */
import type { CSSProperties, ReactNode } from "react";
import { useTheme } from "../../contexts/ThemeContext";

/** A rounded-tile SVG icon with a gradient and a centered glyph. */
export function makeIcon(glyph: string, from: string, to: string) {
  const gradientId = `g-${from}-${to}`.replace(/[^a-zA-Z0-9]/g, "");

  function Tile({ size }: { size: number | string }) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        role="img"
        aria-hidden="true"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <rect
          x="4"
          y="4"
          width="92"
          height="92"
          rx="22"
          fill={`url(#${gradientId})`}
        />
        <text
          x="50"
          y="52"
          dominantBaseline="central"
          textAnchor="middle"
          fontSize="46"
        >
          {glyph}
        </text>
      </svg>
    );
  }

  /** Dock icon — fills its container. */
  function Icon() {
    return <Tile size="100%" />;
  }

  /** Titlebar icon — small fixed size. */
  function TitleIcon() {
    return <Tile size={16} />;
  }

  return { Icon, TitleIcon };
}

/**
 * PortfolioContent — shared layout primitive so every portfolio app feels
 * consistent: a scrollable, theme-aware panel with a title header and body.
 */
export function PortfolioContent({
  title,
  subtitle,
  children,
  maxWidth = 720,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  maxWidth?: number;
}) {
  const { isDark } = useTheme();
  const panel: CSSProperties = {
    height: "100%",
    overflow: "auto",
    background: isDark ? "#1c1c22" : "#ffffff",
    color: isDark ? "#e8e8ea" : "#1a1a1f",
    WebkitOverflowScrolling: "touch",
  };
  return (
    <div style={panel}>
      <div style={{ maxWidth, margin: "0 auto", padding: "28px 24px 48px" }}>
        <header style={{ marginBottom: 20 }}>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>{title}</h1>
          {subtitle ? (
            <p style={{ margin: "6px 0 0", opacity: 0.65, fontSize: 15 }}>
              {subtitle}
            </p>
          ) : null}
        </header>
        {children}
      </div>
    </div>
  );
}

/** A placeholder body used until each app's real content is designed. */
export function PlaceholderBody({ note }: { note: string }) {
  return (
    <div
      style={{
        border: "1px dashed currentColor",
        borderRadius: 12,
        opacity: 0.6,
        padding: "20px 18px",
        fontSize: 14,
        lineHeight: 1.6,
      }}
    >
      {note}
    </div>
  );
}
